// @ts-strict-ignore
import { LabelFormat } from './label-format';
import { ISpanData } from './span-data';
import { StringUtils } from '@shared/util/string-utils';
import { ITableProvider } from './table-provider';
import { ConfiguratorParameters, isDinFont } from '@3d-scene/configurator-parameters/configurator-parameters';
import { ConstraintCheckResults } from './constraint-check-validation/constraint-check-results';
import { ConstraintCheckUtils } from './constraint-check-validation/constraint-check-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

export class LabelEditorUtils {
  public static readonly DEFAULT_FONT_SIZE: number = 10;
  public static readonly DEFAULT_HEX_COLOR: string = '#000000';

  public static createEmptyHTML5Data(fontSize: number, bold: boolean, font: string): string {
    let html5data: string = `<p style="text-align: center;" data-le-font="${font}">`;
    const html5DataFontSize: number = LabelEditorUtils.convertFontSizeToHTML5DataFontSize(fontSize);

    // Don't use styles on din fonts.
    const useBold = bold && !isDinFont(font);
    if (useBold) {
      html5data += '<strong';
    } else {
      html5data += '<span';
    }

    if (html5DataFontSize === LabelEditorUtils.DEFAULT_FONT_SIZE) {
      html5data += '>';
    } else {
      html5data += ' style="font-size: ' + html5DataFontSize + 'px;">';
    }

    // special char &#65279; is used (as it is also sometimes used by quill editor itself) to allow formatting of empty cells,
    // we also need to keep default stylings (like bold and font size) without any chars, such formatting is not kept.
    // This would be extremely problematic for normal users, who cannot adjust for example bold or font size manually.
    html5data += '&#65279;';

    if (useBold) {
      html5data += '</strong>';
    } else {
      html5data += '</span>';
    }
    html5data += '</p>';
    return html5data;
  }

  public static extractTextFromHTML5Data(html5data: string): string {
    if (html5data === null || html5data === '') {
      return '';
    }
    const span = document.createElement('span');
    html5data = html5data.replace(new RegExp('</p>', 'g'), '#linebreak</p>');
    span.innerHTML = html5data;
    let text: string = span.innerText;
    text = text.replace(new RegExp('#linebreak', 'g'), '\n');
    return text;
  }

  public static isHTML5Data(s: string): boolean {
    if (StringUtils.isEmpty(s)) {
      return false;
    }
    return s.indexOf('<p') >= 0;
  }

  private static findTargetElementForTextReplacement(element: HTMLParagraphElement): HTMLElement {
    let targetElement: HTMLElement = element;
    const strongElements: HTMLCollectionOf<HTMLElement> = element.getElementsByTagName('strong');
    if (strongElements && strongElements.length > 0) {
      targetElement = strongElements[0];
    } else {
      const spanElements: HTMLCollectionOf<HTMLElement> = element.getElementsByTagName('span');
      if (spanElements && spanElements.length > 0) {
        targetElement = spanElements[0];
      }
    }
    return targetElement;
  }

  public static replaceTextInHTML5Data(html5data: string, newText: string, trimLinesInNewText: boolean = false): string {
    if (!html5data || html5data.trim().length === 0) {
      html5data = '<p></p>';
    }
    if (!newText) {
      newText = '';
    }
    const parser: DOMParser = new DOMParser();
    const html5dataDocument: Document = parser.parseFromString("<div id='content'>" + html5data + '</div>', 'text/html');
    const content: Element = html5dataDocument.getElementById('content');
    const paragraphElements: HTMLCollectionOf<HTMLParagraphElement> = !html5dataDocument ? null : html5dataDocument.getElementsByTagName('p');
    const element: HTMLParagraphElement = paragraphElements === null || paragraphElements.length === 0 ? null : paragraphElements[0];
    if (element) {
      const targetElement: HTMLElement = this.findTargetElementForTextReplacement(element);
      if (targetElement) {
        // remove old paragraphs (first paragraph is kept)
        for (let i: number = paragraphElements.length - 1; i > 0; i--) {
          paragraphElements[i].parentNode.removeChild(paragraphElements[i]);
        }
        const newTextLines: string[] = newText.split('\n');
        const firstLine: string = newTextLines.length === 0 ? '' : newTextLines[0];
        targetElement.innerHTML = trimLinesInNewText ? firstLine.trim() : firstLine;
        for (let i: number = 1; i < newTextLines.length; i++) {
          const newParagraph: HTMLParagraphElement = <HTMLParagraphElement>element.cloneNode(true);
          const tElement: HTMLElement = this.findTargetElementForTextReplacement(newParagraph);
          tElement.innerHTML = trimLinesInNewText ? newTextLines[i].trim() : newTextLines[i];
          content.appendChild(newParagraph);
        }
        return content.innerHTML;
      } else {
        return html5data; // failed to replace text
      }
    } else {
      return html5data; // failed to replace text
    }
  }

  public static applyCustomFormatSettingsToHTML5Data(
    html5data: string,
    fontSize: number,
    textRotation: number,
    verticalAlign: string,
    textCompression: number,
    autoFormatByTextCheck: boolean,
    containsErrors: boolean,
    containsStrictConformityErrors: boolean,
    errorTooltipText: string,
    font: string,
  ): string {
    if (!html5data || html5data.trim().length === 0) {
      html5data = '<p></p>';
    }
    const parser: DOMParser = new DOMParser();
    const html5dataDocument: Document = parser.parseFromString("<div id='content'>" + html5data + '</div>', 'text/html');

    if([ConfiguratorParameters.labeling.dinFontNameMiddle as string, ConfiguratorParameters.labeling.dinFontNameCondensed as string].includes(font)) {
      const text = LabelEditorUtils.extractTextFromHTML5Data(html5data).trim();
      const useCondensedDinFont = text.length > ConfiguratorParameters.labeling.dinFontMiddleMaxCharacters;
      font = useCondensedDinFont ? ConfiguratorParameters.labeling.dinFontNameCondensed : ConfiguratorParameters.labeling.dinFontNameMiddle
    }

    LabelEditorUtils.applyCustomFormatSettingsToHTML5DataDocument(
      html5dataDocument,
      fontSize,
      textRotation,
      verticalAlign,
      textCompression,
      autoFormatByTextCheck,
      containsErrors,
      containsStrictConformityErrors,
      errorTooltipText,
      font,
    );
    return LabelEditorUtils.getHtml5dataFromDocument(html5dataDocument);
  }

  public static getHtml5dataFromDocument(html5dataDocument: Document): string {
    if (html5dataDocument === null) {
      return null;
    }
    const content: Element = html5dataDocument.getElementById('content');
    return content.innerHTML;
  }

  public static applyCustomFormatSettingsToHTML5DataDocument(
    html5dataDocument: Document,
    fontSize: number,
    textRotation: number,
    verticalAlign: string,
    textCompression: number,
    autoFormatByTextCheck: boolean,
    containsErrors: boolean,
    containsStrictConformityErrors: boolean,
    errorTooltipText: string,
    font: string,
  ): void {
    if (!isNaN(fontSize)) {
      LabelEditorUtils.changeFontSizeInDocument(html5dataDocument, fontSize);
    }
    const element: Element = !html5dataDocument ? null : html5dataDocument.getElementsByTagName('p')[0];
    if (!element) return;

    if (textRotation !== -1) {
      element.setAttribute('data-le-text-rotation', textRotation + '');
    }
    if (verticalAlign !== null) {
      element.setAttribute('data-le-vertical-align', verticalAlign);
    }
    if (textCompression !== -1) {
      element.setAttribute('data-le-text-compression', textCompression + '');
    }
    if (autoFormatByTextCheck !== null) {
      element.setAttribute('data-le-auto-format-by-text-check', autoFormatByTextCheck.toString());
    }
    if (containsErrors !== null) {
      element.setAttribute('data-le-contains-errors', containsErrors.toString());
    }
    if (containsStrictConformityErrors !== null) {
      element.setAttribute('data-le-contains-strict-conformity-errors', containsStrictConformityErrors.toString());
    }
    if (errorTooltipText !== null) {
      element.setAttribute('data-le-error-tooltip-text', errorTooltipText);
    }
    if (font !== null) {
      element.setAttribute('data-le-font', font);
    }
  }

  public static getFontSizeFromHTML5Data(html5data: string): number {
    if (!html5data || html5data.trim().length === 0) {
      return NaN;
    }
    const parser: DOMParser = new DOMParser();
    const html5dataDocument: Document = parser.parseFromString("<div id='content'>" + html5data + '</div>', 'text/html');
    // span, strong, em, u
    const tagNames: string[] = ['span', 'strong', 'em', 'u'];
    let commonFontSize: number = NaN;
    for (const tagName of tagNames) {
      const elements: HTMLCollectionOf<Element> = html5dataDocument.getElementsByTagName(tagName);
      if (elements) {
        for (let i: number = 0; i < elements.length; i++) {
          const element: HTMLElement = elements[i] as HTMLElement;
          if (element.style) {
            const fontSizeString: string = element.style.getPropertyValue('font-size');
            if (fontSizeString) {
              const fontSize: number = parseInt(fontSizeString, 10);
              if (isNaN(commonFontSize)) {
                commonFontSize = fontSize;
              } else {
                if (commonFontSize !== fontSize) {
                  return -1; // no commonly used font size
                }
              }
            }
          }
        }
      }
    }
    if (isNaN(commonFontSize)) {
      commonFontSize = LabelEditorUtils.DEFAULT_FONT_SIZE;
    }
    return LabelEditorUtils.convertHTML5DataFontSizeToFontSize(commonFontSize);
  }

  private static changeFontSizeInDocument(html5dataDocument: Document, newFontSize: number): void {
    if (!html5dataDocument || isNaN(newFontSize)) {
      return;
    }
    newFontSize = LabelEditorUtils.convertFontSizeToHTML5DataFontSize(newFontSize);

    // span, strong, em, u
    const tagNames: string[] = ['span', 'strong', 'em', 'u'];
    for (const tagName of tagNames) {
      const elements: HTMLCollectionOf<Element> = html5dataDocument.getElementsByTagName(tagName);
      if (elements) {
        for (let i: number = 0; i < elements.length; i++) {
          const element: HTMLElement = elements[i] as HTMLElement;
          if (element.style) {
            element.style.removeProperty('font-size');
            if (element.getAttribute('style') === '' || !element.getAttribute('style')) {
              element.removeAttribute('style');
            }
            if (tagName === 'span' && !element.getAttribute('style')) {
              // move all children from old element to new target (parent):
              while (element.childNodes.length > 0) {
                element.parentElement.appendChild(element.childNodes[0]);
              }
              // span-Element with no font-size and no color has no purpose anymore, so we remove it
              element.parentElement.removeChild(element);
              // removing element updates original found node-list, so we need to keep loop var in current place:
              i--;
            }
          }
        }
      }
    }

    // in case of default font size, all we needed to do, is remove all previously existing font size information:
    if (newFontSize === LabelEditorUtils.DEFAULT_FONT_SIZE) {
      return;
    }

    const nodeNames: string[] = ['SPAN', 'STRONG', 'EM', 'U'];
    // p
    const paragraphElements: HTMLCollectionOf<HTMLParagraphElement> = html5dataDocument.getElementsByTagName('p');
    if (paragraphElements) {
      for (let i: number = 0; i < paragraphElements.length; i++) {
        const p: HTMLParagraphElement = paragraphElements[i];
        if (p.childNodes.length === 0) {
          const span: HTMLSpanElement = html5dataDocument.createElement('span');
          span.style.setProperty('font-size', newFontSize + 'px');
          p.appendChild(span);
        } else {
          for (let j: number = 0; j < p.childNodes.length; j++) {
            const child = p.childNodes[j];
            if (nodeNames.indexOf(child.nodeName) >= 0) {
              child['style'].setProperty('font-size', newFontSize + 'px');
            } else {
              // in case no appropiate element is present at the beginning of a paragraph we must add new span with font size information:
              const span: HTMLSpanElement = html5dataDocument.createElement('span');
              span.style.setProperty('font-size', newFontSize + 'px');
              p.replaceChild(span, child);
              span.appendChild(child);
            }
          }
        }
      }
    }
  }

  public static parseCustomFormatSettingsIntoSpanDataFromHTML5Data(spanData: ISpanData, html5data: string): void {
    if (spanData === null || html5data === null) {
      return;
    }
    const parser: DOMParser = new DOMParser();
    const html5dataDocument: Document = parser.parseFromString('<div>' + html5data + '</div>', 'text/html');
    const element: Element = !html5dataDocument ? null : html5dataDocument.getElementsByTagName('p')[0];
    let direction: number = 0;
    let verticalAlign: string = LabelFormat.VALIGN_DEFAULT;
    let compression: number = 100;
    let autoFormatByTextCheck: boolean = true;
    let containsErrors: boolean = false;
    let containsStrictConformityErrors: boolean = false;
    let errorTooltipText: string = '';
    if (element) {
      const directionString: string = element.getAttribute('data-le-text-rotation');
      if (directionString && directionString.trim().length > 0) {
        direction = parseInt(directionString, 10);
      }
      const verticalAlignString: string = element.getAttribute('data-le-vertical-align');
      if (verticalAlignString && verticalAlignString.trim().length > 0) {
        verticalAlign = verticalAlignString;
      }
      const compressionString: string = element.getAttribute('data-le-text-compression');
      if (compressionString && compressionString.trim().length > 0) {
        compression = parseInt(compressionString, 10);
      }
      const autoFormatByTextCheckString: string = element.getAttribute('data-le-auto-format-by-text-check');
      if (autoFormatByTextCheckString && autoFormatByTextCheckString.trim().length > 0) {
        autoFormatByTextCheck = StringUtils.parseBoolean(autoFormatByTextCheckString);
      }
      const containsErrorsString: string = element.getAttribute('data-le-contains-errors');
      if (containsErrorsString && containsErrorsString.trim().length > 0) {
        containsErrors = StringUtils.parseBoolean(containsErrorsString);
      }
      const containsStrictConformityErrorsString: string = element.getAttribute('data-le-contains-strict-conformity-errors');
      if (containsStrictConformityErrorsString && containsStrictConformityErrorsString.trim().length > 0) {
        containsStrictConformityErrors = StringUtils.parseBoolean(containsStrictConformityErrorsString);
      }
      const errorTooltipTextString: string = element.getAttribute('data-le-error-tooltip-text');
      if (errorTooltipTextString && errorTooltipTextString.trim().length > 0) {
        errorTooltipText = errorTooltipTextString;
      }
    }
    spanData.labelFormat.direction = direction;
    spanData.labelFormat.verticalAlign = verticalAlign;
    spanData.compression = compression;
    spanData.autoFormatByTextCheck = autoFormatByTextCheck;
    spanData.containsErrors = containsErrors;
    spanData.containsStrictConformityErrors = containsStrictConformityErrors;
    spanData.errorTooltipText = errorTooltipText;
  }

  public static convertDataToHTML5DataDocument(data: string, spanData?: ISpanData): Document {
    if (!data) {
      return null;
    }
    let modData: string = data;

    // remove styles if din-font is used.
    if (!spanData || isDinFont(spanData.font)) {
      modData = StringUtils.replaceByRegex(modData, new Map([
        [/<I>/gi, ''],
        [/<\/I>/gi, ''],
        [/<B>/gi, ''],
        [/<\/B>/gi, '']
      ]))
    }

    // pre-convert some simple cases:
    modData = StringUtils.replaceByRegex(modData, new Map([
      [/<I>/gi, '<em>'],
      [/<\/I>/gi, '</em>'],
      [/<B>/gi, '<strong>'],
      [/<\/B>/gi, '</strong>'],
      [/<textformat.*?>/gi, ''],
      [/<\/textformat.*?>/gi, '']
    ]));

    const parser: DOMParser = new DOMParser();
    const sourceDocument: Document = parser.parseFromString("<div id='content'>" + modData + '</div>', 'text/html');

    this.convertFontElements(sourceDocument);
    this.updateParagraphElements(sourceDocument);

    return sourceDocument;
  }

  private static convertFontElements(sourceDocument: Document): void {
    // replace all <font> elements with a new <span> that contains the needed styles for color and font size.
    const elements = Array.from(sourceDocument.querySelectorAll<HTMLElement>('*'));
    const legacyFontElements = elements.filter((element) => element.tagName === 'FONT');
    for (const element of legacyFontElements) {
      const span: HTMLSpanElement = sourceDocument.createElement('span');

      const elementColor: string = element.getAttribute('COLOR');
      if (elementColor !== LabelEditorUtils.DEFAULT_HEX_COLOR) {
        span.style.setProperty('color', LabelEditorUtils.convertHexColorStringToRGBColorString(elementColor));
      }

      const html5DataFontSize: number = LabelEditorUtils.convertFontSizeStringToHTML5DataFontSize(element.getAttribute('SIZE'));
      if (html5DataFontSize !== LabelEditorUtils.DEFAULT_FONT_SIZE) {
        span.style.setProperty('font-size', html5DataFontSize + 'px');
      }

      // move all children from old element to new target:
      while (element.childNodes.length > 0) {
        span.appendChild(element.childNodes[0]);
      }

      // switch out old element against new span-tag:
      element.parentElement.replaceChild(span, element);
    }
  }

  private static updateParagraphElements(sourceDocument: Document): void {
    const paragraphElements: HTMLParagraphElement[] = Array.from(sourceDocument.getElementsByTagName('p'));
    for (const p of paragraphElements) {

      // Replace ALIGN-Attribute with the corresponding text-align style.
      const align: string = p.getAttribute('ALIGN');
      p.removeAttribute('ALIGN');
      if (align === 'CENTER') {
        p.style.setProperty('text-align', 'center');
      } else if (align === 'RIGHT') {
        p.style.setProperty('text-align', 'right');
      } else {
        // do nothing thus default of left align is used
      }

      if (StringUtils.isEmpty(p.innerText)) {
        // special char &#65279; needs to be introduced for empty content (even if there are some space-chars)
        // to allow formatting of empty cells:
        const allChildren = p.querySelectorAll('*');
        const deepestChild = allChildren.length === 0 ? null : allChildren[allChildren.length - 1];
        if (deepestChild === null) {
          p.innerHTML = '&#65279;' + p.innerHTML; // + p.innerHTML to keep empty chars
        } else {
          deepestChild.innerHTML = '&#65279;' + deepestChild.innerHTML; // + deepestChild.innerHTML to keep empty chars
        }
      }
    }
  }


  public static convertHTML5DataFontSizeToFontSize(html5DataFontSize: number): number {
    if (isNaN(html5DataFontSize)) {
      return html5DataFontSize;
    }
    return html5DataFontSize / 2;
  }

  public static convertFontSizeToHTML5DataFontSize(fontSize: number): number {
    if (isNaN(fontSize)) {
      return fontSize;
    }
    return fontSize * 2;
  }

  public static convertFontSizeStringToHTML5DataFontSize(fontSizeString: string): number {
    let fontSize: number = LabelEditorUtils.DEFAULT_FONT_SIZE;
    if (fontSizeString) {
      fontSize = parseInt(fontSizeString, 10);
    }
    return LabelEditorUtils.convertFontSizeToHTML5DataFontSize(fontSize);
  }

  public static convertHTML5DataFontSizeStringToFontSize(html5DataFontSizeString: string): number {
    if (html5DataFontSizeString) {
      const html5DataFontSize: number = parseInt(html5DataFontSizeString.replace('px', ''), 10);
      return LabelEditorUtils.convertHTML5DataFontSizeToFontSize(html5DataFontSize);
    } else {
      return LabelEditorUtils.DEFAULT_FONT_SIZE;
    }
  }

  public static convertHTML5DataToData(html5data: string): string {
    if (html5data === null) {
      return null;
    }
    let modData: string = html5data;
    modData = modData.replace(new RegExp('&#65279;', 'g'), ''); // remove all placeholder chars introduced to support formatting in editor
    modData = modData.replace(new RegExp('\uFEFF', 'g'), ''); // remove all placeholder chars introduced to support formatting in editor
    if (modData.trim() === '') {
      return modData;
    }
    const parser: DOMParser = new DOMParser();
    const sourceDocument: Document = parser.parseFromString("<div id='content'>" + modData + '</div>', 'text/html');

    const fontName = this.extractFontNameFromHtml(sourceDocument);

    // span, strong, em, u
    const tagNames: string[] = ['span', 'strong', 'em', 'u'];
    for (const tagName of tagNames) {
      const elements: HTMLCollectionOf<Element> = !sourceDocument ? null : sourceDocument.getElementsByTagName(tagName);
      if (elements) {
        // convert existing elements
        for (let i: number = 0; i < elements.length; i++) {
          i = this.applyHtmlFontAttributes(elements, i, tagName, sourceDocument);
        }
      }
    }

    // p
    const paragraphElements: HTMLCollectionOf<HTMLParagraphElement> = !sourceDocument ? null : sourceDocument.getElementsByTagName('p');
    if (paragraphElements) {
      // convert/cleanup existing paragraphs
      for (let i: number = 0; i < paragraphElements.length; i++) {
        this.applyHtmlParagraphAttributes(paragraphElements[i], sourceDocument, fontName);
      }
    }

    // return converted content
    const content: Element = sourceDocument.getElementById('content');
    let result: string = content.innerHTML;

    for (const attributeName of ['size', 'color', 'face']) {
      result = result.replace(` ${attributeName}="`, ` ${attributeName.toUpperCase()}="`);
    }

    // to make even capitilization identical to old/existing text values:
    result = this.uppercaseHtmlTags(result);

    return result;
  }

  private static uppercaseHtmlTags(result: string) {
    for (const replacementPair of new Map<string, string>([
      ['<i>', '<I>'],
      ['<i>', '<I>'],
      ['</i>', '</I>'],
      ['<em>', '<I>'],
      ['</em>', '</I>'],
      ['<u>', '<U>'],
      ['</u>', '</U>'],
      ['<b>', '<B>'],
      ['</b>', '</B>'],
      ['<strong>', '<B>'],
      ['</strong>', '</B>'],
      ['<font', '<FONT'],
      ['</font', '</FONT'],
      ['<p align', '<P ALIGN'],
      ['</p>', '</P>'],
    ])) {
      result = result.replace(new RegExp(replacementPair[0], 'g'), replacementPair[1]);
    }
    return result;
  }

  private static applyHtmlParagraphAttributes(p: HTMLParagraphElement, sourceDocument: Document, fontName: string) {
    let textAlign: string = p.style.getPropertyValue('text-align');
    // if textAlign is empty try reading quill style class
    if (!textAlign) {
      const styleClass = p.classList.toString();
      if (styleClass.includes('ql-align-center')) {
        textAlign = 'center';
      } else if (styleClass.includes('ql-align-right')) {
        textAlign = 'right';
      } else {
        textAlign = 'left';
      }
    }
    // clear all attributes from paragaph, like custom data information used in editor
    // this also will remove all css styles by removing style attribute
    while (p.attributes.length > 0) {
      p.removeAttribute(p.attributes[0].name);
    }
    if (textAlign === 'center') {
      p.setAttribute('align', 'CENTER');
    } else if (textAlign === 'right') {
      p.setAttribute('align', 'RIGHT');
    } else {
      p.setAttribute('align', 'LEFT');
    }

    if (p.childNodes.length === 0 || p.childNodes[0].nodeName !== 'FONT') {
      // in case no font element is present at the beginning of a paragraph we must add default font stylings:
      const font: HTMLFontElement = sourceDocument.createElement('font');
      font.setAttribute('size', LabelEditorUtils.convertHTML5DataFontSizeToFontSize(LabelEditorUtils.DEFAULT_FONT_SIZE).toString(10));
      font.setAttribute('color', LabelEditorUtils.DEFAULT_HEX_COLOR);
      // move all children from old element to new target:
      while (p.childNodes.length > 0) {
        font.appendChild(p.childNodes[0]);
      }
      p.appendChild(font);
    }

    p.querySelector("font").setAttribute('face', fontName);
  }

  private static applyHtmlFontAttributes(elements: HTMLCollectionOf<Element>, i: number, tagName: string, sourceDocument: Document) {
    const element: HTMLElement = elements[i] as HTMLElement;
    const elementFontSize: string = element.style.getPropertyValue('font-size');
    const elementColor: string = element.style.getPropertyValue('color');
    if ((tagName === 'u' || tagName === 'em' || tagName === 'strong') && !elementFontSize && !elementColor) {
      // no font information present on (simple) <u>-element
      // we keep element (u is translated to u), but we make sure no other attributes are kept
      while (element.attributes.length > 0) {
        element.removeAttribute(element.attributes[0].name);
      }
    } else {
      const font: HTMLFontElement = sourceDocument.createElement('font');
      font.setAttribute('size', LabelEditorUtils.convertHTML5DataFontSizeStringToFontSize(elementFontSize).toString(10));
      font.setAttribute(
        'color',
        elementColor ? LabelEditorUtils.convertRGBColorStringToHexColorString(elementColor) : LabelEditorUtils.DEFAULT_HEX_COLOR
      );
      let newTargetForChildren: HTMLElement = font;
      if (tagName === 'strong') {
        const bold: HTMLElement = sourceDocument.createElement('b');
        font.appendChild(bold);
        newTargetForChildren = bold;
      } else if (tagName === 'em') {
        const italic: HTMLElement = sourceDocument.createElement('i');
        font.appendChild(italic);
        newTargetForChildren = italic;
      } else if (tagName === 'u') {
        const underline: HTMLElement = sourceDocument.createElement('u');
        font.appendChild(underline);
        newTargetForChildren = underline;
      }
      // move all children from old element to new target:
      while (element.childNodes.length > 0) {
        newTargetForChildren.appendChild(element.childNodes[0]);
      }
      // switch out old element against new font-tag:
      element.parentElement.replaceChild(font, element);
      // replacing element updates original found node-list, so we need to keep loop var in current place:
      i--;
    }
    return i;
  }

  private static extractFontNameFromHtml(sourceDocument: Document): string {
    return sourceDocument.querySelector('[data-le-font]')?.getAttribute('data-le-font');
  }

  private static convertRGBColorStringToHexColorString(rgbColorString: string): string {
    if (rgbColorString === null) {
      return null;
    }
    const match = rgbColorString.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
    return match && match.length === 4
      ? '#' +
          ('0' + parseInt(match[1], 10).toString(16)).slice(-2) +
          ('0' + parseInt(match[2], 10).toString(16)).slice(-2) +
          ('0' + parseInt(match[3], 10).toString(16)).slice(-2)
      : '';
  }

  private static convertHexColorStringToRGBColorString(hexColorString: string): string {
    if (hexColorString === null) {
      return null;
    }
    // expand shorthand hex color form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hexColorString = hexColorString.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColorString);
    return match ? 'rgb(' + parseInt(match[1], 16) + ', ' + parseInt(match[2], 16) + ', ' + parseInt(match[3], 16) + ')' : '';
  }

  public static createSymbolOptions(charRanges): Object[] {
    const symbolOptions: Object[] = [];
    for (const charRange of charRanges) {
      if (charRange instanceof Array) {
        for (let i: number = charRange[0]; i <= charRange[1]; i++) {
          symbolOptions.push({ value: i, label: String.fromCharCode(i) });
        }
      } else {
        if (typeof charRange === 'string') {
          symbolOptions.push({ value: charRange, label: charRange });
        } else {
          symbolOptions.push({ value: charRange, label: String.fromCharCode(charRange) });
        }
      }
    }
    return symbolOptions;
  }

  public static calculateCellInsets(
    spanData: ISpanData,
    maxMultilineCount: number,
    cellWidth: number,
    cellHeight: number,
    rowSpan: number = null,
    colSpan: number = null
  ): Object {
    // determine insets, which make visible cell smaller compared to table cell according to size of label material
    if (colSpan === null && spanData) {
      colSpan = spanData.colSpan;
    }
    if (rowSpan === null && spanData) {
      rowSpan = spanData.rowSpan;
    }
    const allowInsets: boolean = spanData && maxMultilineCount <= 1 && !spanData.isMergeWithNext && spanData.colSpan <= 1;
    let insetLeft: number = 0;
    let insetRight: number = 0;
    let insetTop: number = 0;
    let insetBottom: number = 0;
    if (allowInsets) {
      insetLeft = Math.max(0, Math.floor((cellWidth - spanData.labelFormat.width) / 2));
      insetRight = Math.max(0, Math.ceil((cellWidth - spanData.labelFormat.width) / 2));
      insetTop = Math.max(0, Math.floor((cellHeight - spanData.labelFormat.height) / 2));
      insetBottom = Math.max(0, Math.ceil((cellHeight - spanData.labelFormat.height) / 2));
    }
    return { insetLeft: insetLeft, insetRight: insetRight, insetTop: insetTop, insetBottom: insetBottom };
  }

  public static explodeCellRanges(
    tableProvider: ITableProvider,
    cellRanges,
    onlyIncludeEnabledCells: boolean = true,
    forceIncludeEndPlates: boolean = false
  ): number[][] {
    // if forceIncludeEndPlates, then end plates right after the end and inbetween of selected cells are also included in exploded range
    // end plates right before selected cells are not included, as they are considered to be belongig to cells before that end plate.
    const explodedCellRanges: number[][] = [];
    if (!cellRanges || cellRanges.length === 0) {
      return explodedCellRanges;
    }
    // TODO: maybe it would be better to check, that with forceIncludeEndPlates cells are not included twice in exploded range
    for (const cellRange of cellRanges) {
      let maxCol: number = Math.max(cellRange[1], cellRange[3]) + (forceIncludeEndPlates ? 1 : 0);
      for (let r = Math.min(cellRange[0], cellRange[2]); r <= Math.max(cellRange[0], cellRange[2]); r++) {
        for (let c = Math.min(cellRange[1], cellRange[3]); c <= maxCol; c++) {
          const spanData: ISpanData = tableProvider.getSpanData(r, c);
          if (
            spanData &&
            (spanData.parent === null || (forceIncludeEndPlates && spanData.isEndPlate)) &&
            (!forceIncludeEndPlates || c < maxCol || (forceIncludeEndPlates && spanData.isEndPlate))
          ) {
            if (onlyIncludeEnabledCells) {
              if (spanData.enabled || (forceIncludeEndPlates && spanData.isEndPlate)) {
                explodedCellRanges.push([r, c]);
                if (c === maxCol && forceIncludeEndPlates && spanData.isEndPlate) {
                  maxCol++; // in case we find endplate at the end of a selection, we need to look also in next column, to cover cases with multiple endplates right after each other
                }
              }
            } else {
              explodedCellRanges.push([r, c]);
            }
          }
        }
      }
    }
    return explodedCellRanges;
  }

  public static getParentRowIndexes(lineSpan: number, multilineCount: number): number[] {
    if (lineSpan <= 0 || multilineCount <= 0 || lineSpan < multilineCount || lineSpan % multilineCount !== 0) {
      return null;
    }
    const parentRowIndexes: number[] = [];
    const step: number = lineSpan / multilineCount;
    for (let i = 0; i < multilineCount; i++) {
      parentRowIndexes.push(i * step);
    }
    return parentRowIndexes;
  }

  /**
   * Convenience wrapper: applies the check results of a `performValidationOnSpanData` call together with the
   * formatting properties of `spanData` to the given `html5data` string.
   * Use this instead of calling `applyCustomFormatSettingsToHTML5Data` with the repeated standard argument pattern.
   */
  public static applyCheckResultsToHTML5Data(
    html5data: string,
    spanData: ISpanData,
    checkResults: ConstraintCheckResults,
  ): string {
    return LabelEditorUtils.applyCustomFormatSettingsToHTML5Data(
      html5data,
      LabelEditorUtils.getEffectiveFontSize(spanData, checkResults),
      spanData.labelFormat.direction,
      spanData.labelFormat.verticalAlign,
      spanData.autoFormatByTextCheck && !Number.isNaN(checkResults.textCompression) ? checkResults.textCompression : spanData.compression,
      spanData.autoFormatByTextCheck,
      checkResults.containsErrors,
      checkResults.containsStrictConformityErrors,
      checkResults.errorTooltipText,
      spanData.font,
    );
  }

  /**
   * Convenience wrapper: combines `performValidationOnSpanData` (with the standard defaults -1/NaN/null for
   * rotation/compression/autoFormat) and `applyCheckResultsToHTML5Data` into a single call.
   * Use this for the common case where only `html5data` changes and all format parameters stay as-is on the spanData.
   */
  public static validateAndApplyHTML5Data(
    html5data: string,
    spanData: ISpanData,
    useStrictConformity: boolean,
    localeResourceManager: LocaleResourceManager,
  ): string {
    const checkResults: ConstraintCheckResults = ConstraintCheckUtils.performValidationOnSpanData(
      useStrictConformity,
      localeResourceManager,
      spanData,
      -1,
      Number.NaN,
      null,
      html5data,
    );
    return LabelEditorUtils.applyCheckResultsToHTML5Data(html5data, spanData, checkResults);
  }

  public static getEffectiveFontSize(spanData: ISpanData, checkResults: ConstraintCheckResults): number {
    return spanData.autoFormatByTextCheck ? spanData.uniformFontSize ?? checkResults.fontSize : Number.NaN;
  }
}
